import React from "react";
import { OrderDto, CompanyDto } from "../../../../api";
import { ColDef, ICellRendererParams, ITooltipParams } from "ag-grid-community";
import styled from "styled-components";
import { ProductInformationLinkParameters } from "../containers/OrderDetailsView";
import { Grid, createHeaderAndHeaderTooltip } from "../../../../framework/components/grid/Grid";
import {
  GridIconRenderer,
  GridIconRendererProps
} from "../../../../framework/components/grid/GridIconRenderer";
import { supplyingPlantField } from "../containers/OrdersView";
import { useTranslation } from "react-i18next";

export interface OrderInfoProps {
  order: OrderDto | undefined;
  company?: CompanyDto | undefined;
  columnDefs: ColDef[];
  hasProductInformationAccessRight: boolean;
  hasOrderTrackingRights: boolean;
  hasTestCertificates: boolean;
  hasSoftwareDocuments: boolean;
  goToProductInformation: (params: ProductInformationLinkParameters) => void;
}

const OrderInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const getCompanyCol = (t: (text: string) => string) => {
  return {
    valueGetter: (params: any): string => {
      const company = params.data as CompanyDto;
      return `${company.name}, ${company.addressCity}, ${company.countryCode} (${company.id})`;
    },
    headerName: t("Company"),
    headerTooltip: t("Company"),
    width: 300,
    tooltipValueGetter: (params: any) => {
      const company = params.data as CompanyDto;
      return `${company.name}, ${company.addressCity}, ${company.countryCode} (${company.id})`;
    }
  };
};

export const OrderInfo: React.FunctionComponent<OrderInfoProps> = (props) => {
  const { t } = useTranslation();

  if (props.order === undefined) {
    return null;
  }

  return (
    <OrderInfoContainer>
      <Grid
        onFirstDataRendered={(params) => {
          const columns = params.columnApi.getAllColumns();
          if (columns) {
            params.api.sizeColumnsToFit();
          }
        }}
        columnDefs={props.columnDefs.map((colDef) => {
          if (colDef.field === "productInformation") {
            return {
              field: "hasProductItems",
              hide:
                !props.hasProductInformationAccessRight &&
                !props.hasTestCertificates &&
                !props.hasSoftwareDocuments,
              ...createHeaderAndHeaderTooltip(t("Prod. Info."), t("Product information")),
              headerClass: "grid-header-center-with-menu",
              maxWidth: 130,
              cellRenderer: GridIconRenderer,
              cellRendererParams: (params: ICellRendererParams) => {
                const row = params.data as OrderDto;
                const cellRendererParams: GridIconRendererProps = {
                  ...params,
                  showIcon: props.hasTestCertificates || props.hasSoftwareDocuments,
                  onClick: () => {
                    props.goToProductInformation({
                      orderAcknowledgementNumber: row.orderAcknowledgementNumber
                    });
                  },
                  icon: "abb/double-right"
                };
                return cellRendererParams;
              },
              tooltipValueGetter: (params: ITooltipParams) => {
                if (!params.data) {
                  return "";
                }
                const availableDocumentTypes: string[] = [];
                if (props.hasSoftwareDocuments) {
                  availableDocumentTypes.push(t("Software available"));
                }
                if (props.hasTestCertificates) {
                  availableDocumentTypes.push(t("Test certificates available"));
                }
                const ret = availableDocumentTypes.join(", ");
                return ret;
              }
            };
          } else if (colDef.field === supplyingPlantField) {
            return { ...colDef, hide: !props.hasOrderTrackingRights };
          }
          return colDef;
        })}
        rowData={[{ ...props.company, ...props.order }]}
        domLayout="autoHeight"
        t={t}
      />
    </OrderInfoContainer>
  );
};
